import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import CommentCard from './CommentCard';
import { CommentListSkeleton } from './Skeletons';
import CreateComment from './CreateComment';
import { useSocket } from '../contexts/SocketContext';

const CommentsSection = ({
  postId,
  postAuthorId,
  onCommentAdded,
  onCommentDeleted,
  isMobile = false,
  highlightedCommentId = null,
  showTopComposer = true,
  onReplyTo = null,
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('latest'); // 'latest' | 'top'
  const socket = useSocket();

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/posts/${postId}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Fetch comments error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (postId) {
      fetchComments();
    }
  }, [postId]);

  useEffect(() => {
    if (socket) {
      const handleNewComment = (data) => {
        if (String(data.postId) === String(postId)) {
          fetchComments();
          if (onCommentAdded) onCommentAdded();
        }
      };

      const handlePostDeleted = (data) => {
        if (data.postId) {
          setComments((prev) => prev.filter((c) => c.id !== data.postId));
        }
      };

      socket.on('new-comment', handleNewComment);
      socket.on('post-deleted', handlePostDeleted);

      return () => {
        socket.off('new-comment', handleNewComment);
        socket.off('post-deleted', handlePostDeleted);
      };
    }
  }, [socket, postId, onCommentAdded]);

  const handleCommentCreated = () => {
    fetchComments();
    if (onCommentAdded) onCommentAdded();
  };

  const handleCommentDeleted = (commentId) => {
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    if (onCommentDeleted) onCommentDeleted();
  };

  const sortedComments = useMemo(() => {
    if (!comments || comments.length === 0) return [];
    const list = [...comments];
    if (sortBy === 'top') {
      return list.sort((a, b) => {
        const scoreA = (Number(a.likesCount) || 0) * 2 + (Number(a.repliesCount) || 0);
        const scoreB = (Number(b.likesCount) || 0) * 2 + (Number(b.repliesCount) || 0);
        return scoreB - scoreA;
      });
    }
    // Default 'latest': newest first
    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [comments, sortBy]);

  return (
    <div className="mt-2">
      {/* Top Comment Composer (Only shown when showTopComposer is true) */}
      {showTopComposer && (
        <div className="mb-4 bg-zinc-50/70 dark:bg-zinc-900/40 rounded-2xl p-3 border border-zinc-200/70 dark:border-zinc-800/70 shadow-sm">
          <CreateComment postId={postId} onCommentCreated={handleCommentCreated} />
        </div>
      )}

      {/* Discussion Header & Sort Pills */}
      <div className="flex items-center justify-between py-2.5 border-b border-zinc-100 dark:border-zinc-800/80 mb-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[15px] text-zinc-900 dark:text-zinc-100">
            Discussion
          </span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
            {comments.length}
          </span>
        </div>

        {comments.length > 1 && (
          <div className="flex items-center bg-zinc-100 dark:bg-zinc-800/70 rounded-full p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setSortBy('latest')}
              className={`px-3 py-1 rounded-full font-medium transition-all ${
                sortBy === 'latest'
                  ? 'bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'
              }`}
            >
              Latest
            </button>
            <button
              type="button"
              onClick={() => setSortBy('top')}
              className={`px-3 py-1 rounded-full font-medium transition-all ${
                sortBy === 'top'
                  ? 'bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'
              }`}
            >
              Top
            </button>
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading ? (
        <CommentListSkeleton />
      ) : sortedComments.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
          <div className="w-12 h-12 rounded-2xl bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center mb-3 text-zinc-400 dark:text-zinc-500">
            <i className="far fa-comments text-xl" />
          </div>
          <p className="text-zinc-800 dark:text-zinc-200 font-semibold text-sm mb-1">
            No comments yet
          </p>
          <p className="text-zinc-400 dark:text-zinc-500 text-xs max-w-xs">
            Be the first to join the conversation and share your thoughts!
          </p>
        </div>
      ) : (
        /* Comments list */
        <div className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
          {sortedComments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              postId={postId}
              postAuthorId={postAuthorId}
              onUpdate={fetchComments}
              onDelete={handleCommentDeleted}
              highlightedCommentId={highlightedCommentId}
              isMobileDetail={isMobile}
              onReplyTo={onReplyTo}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
